import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddressAutocomplete from "../../../components/AddressAutocomplete";
import Button from "../../../components/Button";
import AuthCard from "../../../components/AuthCard";
import RequiredLabel from "../../../components/RequiredLabel";
import api from "../../../utils/api";
import { setVendor } from "../../../redux/slices/authSlice";
import { showNotification } from "../../../redux/slices/notificationSlice";

export default function SetPickupAddress() {
  const [address, setAddress] = useState("");
  const [addressMeta, setAddressMeta] = useState({ placeId: null, lat: null, lng: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAddressFilled = address.trim() !== "";
  const requiredReason = t("pleaseCompleteRequiredFields");

  const handleSubmit = async () => {
    if (!isAddressFilled || isSubmitting) return;
    setIsSubmitting(true);
    try {
      let metaForSave = { ...addressMeta };
      const hasCoords = typeof metaForSave.lat === 'number' && typeof metaForSave.lng === 'number';
      if (address && !hasCoords) {
        try {
          const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
          if (key) {
            const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${key}`;
            const resp = await fetch(url);
            const data = await resp.json();
            const loc = data?.results?.[0]?.geometry?.location;
            if (data?.status === 'OK' && loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
              metaForSave.lat = loc.lat;
              metaForSave.lng = loc.lng;
              metaForSave.placeId = data?.results?.[0]?.place_id || metaForSave.placeId || null;
            }
          }
        } catch {}
      }

      if (address && !(typeof metaForSave.lat === 'number' && typeof metaForSave.lng === 'number')) {
        dispatch(showNotification({ message: t('pleaseSelectAddressSuggestion') || 'Please select an address from suggestions so we can set pickup coordinates.', type: 'error' }));
        return;
      }

      const res = await api.patch("/vendor/store/edit", {
        address,
        addressPlaceId: metaForSave.placeId || undefined,
        addressLat: typeof metaForSave.lat === 'number' ? metaForSave.lat : undefined,
        addressLng: typeof metaForSave.lng === 'number' ? metaForSave.lng : undefined,
      });
      const vendorToStore = {
        ...res.data.vendor,
        addressPlaceId: metaForSave.placeId || res.data.vendor.addressPlaceId,
        addressLat: metaForSave.lat ?? res.data.vendor.addressLat,
        addressLng: metaForSave.lng ?? res.data.vendor.addressLng,
      };
      localStorage.setItem("vendor", JSON.stringify(vendorToStore));
      dispatch(setVendor(vendorToStore));
      dispatch(showNotification({ message: t('changesSaved'), type: "success" }));
      navigate("/dashboard");
    } catch (err) {
      dispatch(showNotification({ message: t('failedToSaveChanges'), type: "error" }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50 animate-fade-in">
      <AuthCard>
        <h2 className="sub-header mb-4">{t('pickupAddress')}</h2>
        <RequiredLabel htmlFor="pickup-address" className="mb-2">
          {t("pickupAddress")}
        </RequiredLabel>
        <AddressAutocomplete
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onSelect={(meta) => setAddressMeta({ placeId: meta.placeId, lat: meta.lat, lng: meta.lng })}
          placeholder={t('enterAddress')}
          inputProps={{ id: "pickup-address", required: true }}
        />
        <Button
          onClick={handleSubmit}
          className="w-full mt-4"
          disabled={!isAddressFilled || isSubmitting}
          disabledReason={!isAddressFilled ? requiredReason : ""}
          loading={isSubmitting}
          loadingLabel={t("saving")}
        >
          {t('finish')}
        </Button>
      </AuthCard>
    </div>
  );
}
